import { useState } from 'react';
import { locationDetailsApi } from '@/domains/landing-page/apis/location-details';
import type { LocationSubmitData } from '@/domains/landing-page/types/location-details.types';

type Status = 'idle' | 'locating' | 'submitting' | 'success' | 'error';

export const useLocationDetails = (token?: string) => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<LocationSubmitData | null>(null);

  const shareLocation = () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('This link is invalid.');
      return;
    }
    if (!('geolocation' in navigator)) {
      setStatus('error');
      setErrorMessage('Your browser does not support location sharing.');
      return;
    }

    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setStatus('submitting');
        try {
          const res = await locationDetailsApi.submitLocation(token, { latitude, longitude });
          setResult(res?.data ?? { latitude, longitude, address: null });
          setStatus('success');
        } catch (error: unknown) {
          setStatus('error');
          setErrorMessage(
            (error as Error)?.message ?? 'Failed to share location. Please try again.'
          );
        }
      },
      (geoError) => {
        setStatus('error');
        setErrorMessage(
          geoError.code === geoError.PERMISSION_DENIED
            ? 'Location permission was denied. Please allow location access and try again.'
            : 'Could not determine your location. Please try again.'
        );
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return { status, errorMessage, result, shareLocation };
};
